import { useEffect, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import compact from 'lodash/compact';
import ResizePanel from 'react-resize-panel-ts';

import * as K from 'common/constants';
import { useEventCache } from 'hooks/useLRQ/useEventCache';

import Sidebar from 'components/search/Sidebar/Sidebar';
import SidebarToggle from 'components/search/Sidebar/SidebarToggle';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import SearchCanceledPlaceholder from 'components/common/SearchCanceledPlaceholder/SearchCanceledPlaceholder';
import { HintWithLink } from 'components/common/SearchCanceledPlaceholder/SearchCanceledHints';
import { useResizeRegistration } from 'components/common/Managers/LayoutStateManager';
import { settingsState } from 'components/common/Managers/SettingsManager.state';

import LogDetailsContainer from 'components/search/SearchLogsPage/LogDetailsContainer';
import LogResultsContainer from 'components/search/SearchLogsPage/LogResultsContainer';
import LogResultsTabBar from 'components/search/SearchLogsPage/LogResultsTabBar';
import LogResultsToolbar from 'components/search/SearchLogsPage/LogResultsToolbar';
import LogResultsTimelineContainer from 'components/search/SearchLogsPage/LogResultsTimelineContainer';
import { getLrqErrorMessage, getRequestIds } from 'common/util/lrqRequest';
import {
  anchorLogRequestsState,
  initialLogRequestsState,
  paginatedLogRequestsState,
  searchPageStatusSelector,
} from 'components/search/SearchLogsPage/SearchStateManager.state';

import './SearchLogsResultsArea.scss';

/**
 * This component represents the portion of the Search Logs Page below the search toolbar (the sidebar, the log results
 * toolbar, and the log results container).
 */
function SearchLogsResultsArea() {
  const settings = useRecoilValue(settingsState);
  const setAllSearchPageStatuses = useSetRecoilState(searchPageStatusSelector);
  const sideBarContainerRef = useResizeRegistration();
  const logDetailsRef = useResizeRegistration();
  const sidebarToggleRef = useRef(null);

  // Page-level error handling. Compile a list of request IDs that we can use to check for errors.

  const initialLogRequests = useRecoilValue(initialLogRequestsState);
  const anchorLogRequests = useRecoilValue(anchorLogRequestsState);
  const paginatedLogRequests = useRecoilValue(paginatedLogRequestsState);

  const allRequests = [ ...initialLogRequests, ...anchorLogRequests, ...paginatedLogRequests ];
  const ids = getRequestIds(allRequests);
  const requestCanceled = allRequests.some((request) => request?.canceled);

  function renderResultsContainer() {
    let content = (
      <>
        <LogResultsTabBar />
        <LogResultsTimelineContainer />
        <LogResultsContainer
          sidebarToggleRef={sidebarToggleRef}
          logDetailsRef={logDetailsRef}
        />
      </>
    );
    if (requestCanceled) {
      content = (
        <SearchCanceledPlaceholder
          containerClassName={['SearchCanceledPlaceholder__Container--WithPadding']}
          message="Search Canceled"
          hint={<HintWithLink href={K.ROUTE.EVENTS} />}
        />
      );
    }

    return (
      <div className="SearchLogs__ResultsContainer">
        <LogResultsToolbar />
        {content}
      </div>
    );
  }

  const { errors } = useEventCache(ids);
  const errorList = compact(errors);
  const isError = errorList.length > 0 && !requestCanceled;
  let errorContainer;
  if (isError) {
    const errorObject = {
      message: getLrqErrorMessage(errorList),
      title: 'Invalid Search',
    };
    errorContainer = <ErrorPlaceholder error={errorObject} />;
  }

  /**
   * Handle component statuses
   */
  useEffect(() => {
    if (requestCanceled) {
      // @ts-expect-error
      setAllSearchPageStatuses(K.COMPONENT_STATUS.RESOLVED);
    }

    if (isError) {
      // @ts-expect-error
      setAllSearchPageStatuses(K.COMPONENT_STATUS.REJECTED);
    }
  }, [isError, requestCanceled, setAllSearchPageStatuses]);

  return (
    <>
      <div
        ref={sideBarContainerRef}
        className="SearchLogs__SidebarContainer"
        data-resize-id={K.RESIZE_ID.SEARCH_LOGS_SIDEBAR}
      >
        {settings[K.SETTING.SHOW_SIDEBAR] ? (
          <ResizePanel direction="e" handleClass="SearchLogsResultsArea__ResizeCustomHandle">
            <div className="SearchLogsResultsArea__Sidebar">
              <Sidebar mode={K.EVENT_MODE.LOG} />
              <SidebarToggle toggleRef={sidebarToggleRef} />
            </div>
          </ResizePanel>
        ) : (
          <SidebarToggle toggleRef={sidebarToggleRef} />
        )}
      </div>
      {errorContainer || renderResultsContainer()}

      <div ref={logDetailsRef} data-resize-id={K.RESIZE_ID.SEARCH_LOG_DETAILS}>
        <LogDetailsContainer></LogDetailsContainer>
      </div>
    </>
  );
}

export default SearchLogsResultsArea;
